import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { API, ShareAPI } from '../../API';
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { format, set, add, parseISO } from 'date-fns';
import { cutString } from '../../utils/cutString';
import { palette } from '../../utils/colors';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { platformMapping } from '../../utils/platforms';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { useCurrentUser } from '../../contexts/CurrentUser';
import './TrendChart.scss';
import { ampli } from '../../ampli';
import { showRequestError } from '../../utils/requestError';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../CustomDashboard/WidgetRenderer/echartsUtils';

// echarts SVG text cannot read CSS variables, so the palette is mirrored here.
const AXIS_TEXT_COLOR = '#9EA1B4';
const GRID_LINE_COLOR = '#e1e5ee';
const CROSSHAIR_COLOR = '#1e88e5';
// Drawn into the SVG so PNG/SVG downloads keep a white background.
const CHART_BACKGROUND_COLOR = '#fff';
const CHART_HEIGHT = 450;
const AREA_OPACITY = 0.3;

// Subtle overlay while the parent refetches: no spinner text, just a light veil.
const LOADING_OPTION = {
  text: '',
  color: CROSSHAIR_COLOR,
  maskColor: 'rgba(255, 255, 255, 0.55)',
  spinnerRadius: 8,
  lineWidth: 2,
  zlevel: 0,
};

export const EvolutionTrendChart = ({
  narrative,
  story,
  rawdata = null,
  metric,
  breakdown,
  showLegend = false,
  narrativeColor,
  view,
  showModal = true,
  isReport = false,
  chartRef = null,
  isShare,
  isLoading = false,
  animation = true,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [chartData, setChartData] = useState(null);
  const [modal, setModal] = useState({
    isVisible: false,
    startDate: null,
    endDate: null,
    platform: null,
    country: null,
    sourceGroup: null,
    sourceGroupExclude: false,
    title: '',
  });
  const [countries, setCountries] = useState(null);
  const [addToCaseModal, setAddToCaseModal] = useState({
    isOpen: false,
    countries: null,
    platforms: null,
    countriesExclude: null,
    narrative: null,
    sourceGroup: null,
    sources: null,
    sourceGroupExclude: false,
  });
  const [addToGroupModal, setAddToGroupModal] = useState({
    isOpen: false,
    countries: null,
    platforms: null,
    countriesExclude: null,
    narrative: null,
    sourceGroup: null,
    sources: null,
    sourceGroupExclude: false,
  });

  const api = isShare ? ShareAPI : API;

  const getNarrativeEntity = (entityIndex) => {
    if (!isReport) {
      return;
    }
    let index = entityIndex - 1;

    return rawdata?.objects[index];
  };

  const getAllCountries = useCallback(() => {
    api.fetch('GET', `/API/v1/countries`).then((data) => {
      setCountries(
        (data?.objects ?? []).map((country) => {
          return { value: country.id, label: country.name };
        }),
      );
    }).catch((error) => showRequestError(error, t('Countries')));
  }, [api, t]);

  useEffect(getAllCountries, [getAllCountries]);

  const getCountryId = (countryName) => {
    if (countries?.length > 0) {
      const country = countries.find(
        (country) => country.label === countryName,
      );
      return country;
    }
  };

  const getEndDate = (startDate) => {
    if (!startDate) return null;
    let aggregation = rawdata?.objects[0]?.aggregation;
    if (aggregation === 'DAY') {
      return set(startDate, { days: 1 });
    } else if (aggregation === 'WEEK') {
      return add(startDate, { days: 6 });
    } else if (aggregation === 'MONTH') {
      return add(startDate, { months: 1 });
    }
  };

  const getAllCountriesId = (data) => {
    let countriesList = data
      .filter((item) => item.name !== 'Others')
      .map((item) => { return {value: item.id, label: item.name}});
    if (countriesList.length === 0) {
      return null;
    }
    
    return countriesList;
  };

  const filterPlatforms = () => {
    if (breakdown !== 'PLATFORM') {
      return;
    }
    let allPlatforms = [
      'TELEGRAM',
      'FACEBOOK',
      'VK',
      'TWITTER',
      'WEB',
      'YOUTUBE',
      'TIKTOK',
      'THREADS',
      'INSTAGRAM',
      'OK',
      'LINKEDIN',
    ];
    let dataPlatforms = rawdata?.objects?.map((item) => item.name);
    let platforms = allPlatforms.filter(
      (platform) => !dataPlatforms.includes(platform),
    );
    return platforms.map((platform) => {
      return { value: platform, label: platform };
    });
  };

  const getSourceGroupId = (index) => {
    if (rawdata?.objects?.length > 0) {
      const sourceGroup = rawdata.objects.find(
        (group, idx) => idx === index,
      );
      return [sourceGroup.id];
    }
  }

  const getOthersSourceGroupsIds = () => {
    let sourceGroupsList = rawdata?.objects
      .filter((item) => item?.name !== 'Others')
      .map((item) => item?.id);
    if (sourceGroupsList.length === 0) {
      return null;
    }
    
    return sourceGroupsList;
  };

  // Builds one x category per distinct date (sorted) and one series per entity,
  // filling missing dates with 0 as the previous google-charts version did.
  useEffect(() => {
    if (!rawdata) return;
    const entities = rawdata?.objects ?? [];
    if (!metric) {
      setChartData(null);
      return;
    }

    const dateSet = new Set();
    const valuesByEntity = entities.map((entity) => {
      const values = {};
      (entity.stats ?? []).forEach((stat) => {
        const dateKey = new Date(stat.date_publicated).toISOString().split('T')[0];
        dateSet.add(dateKey);
        const value = stat[metric] || 0;
        values[dateKey] = metric === 'narrative_share' ? Number(Number(value).toFixed(2)) : value;
      });
      return values;
    });

    const dates = Array.from(dateSet).sort();
    setChartData({
      dates,
      dateLabels: dates.map((dateKey) => format(parseISO(dateKey), 'dd.MM.yy')),
      series: entities.map((entity, index) => ({
        name: entity.name,
        data: dates.map((dateKey) => valuesByEntity[index][dateKey] ?? 0),
      })),
    });
  }, [rawdata, metric]);

  const showValues = view?.value === 'VALUES' || view?.value === 'POINTS';
  // A single date has no line segment to draw, so the dot must always be
  // visible there, not only on hover.
  const isSingleDate = chartData?.dates.length === 1;
  const showPoints = view?.value === 'POINTS' || isSingleDate;

  // Options are merged (not replaced) so series with the same id morph into
  // new data. A dataset with fewer series would leave stale ones behind, so
  // every slot ever used is kept and filled with an empty, invisible series.
  const seriesSlotsRef = useRef(0);

  const option = useMemo(() => {
    if (!chartData) return null;
    const slots = Math.max(seriesSlotsRef.current, chartData.series.length);
    seriesSlotsRef.current = slots;
    const seriesNames = chartData.series.map((entry) => entry.name);
    const emptySeries = Array.from({ length: slots - chartData.series.length }, (_, i) => ({
      id: `series-${chartData.series.length + i}`,
      name: '',
      type: 'line',
      data: [],
      silent: true,
      showSymbol: false,
      lineStyle: { width: 0 },
      areaStyle: { opacity: 0 },
      label: { show: false },
      tooltip: { show: false },
    }));
    return {
      color: palette,
      backgroundColor: CHART_BACKGROUND_COLOR,
      // Off for the PDF export, so printing never captures a half-drawn chart.
      animation,
      aria: { enabled: true },
      // Smooth in-place transition when the dataset changes (breakdown /
      // aggregation switch); series are merged by index and morph.
      animationDurationUpdate: 500,
      animationEasingUpdate: 'cubicOut',
      // Left-aligned legend with short line swatches, as the old chart drew it.
      legend: {
        show: true,
        type: 'scroll',
        top: 8,
        left: '5%',
        icon: 'rect',
        itemWidth: 28,
        itemHeight: 5,
        itemGap: 16,
        textStyle: { color: AXIS_TEXT_COLOR, fontSize: 14 },
        formatter: (name) => cutString(name, 60),
        // Explicit so the empty padding series never show up in the legend.
        data: seriesNames,
        // Legend clicks open the series' content modal (see onEvents) instead
        // of toggling the series; the default toggle is reverted on click.
        triggerEvent: true,
      },
      // Same as the old chartArea (5% sides, 15% top/bottom) with the axis
      // labels drawn inside those margins, so the plot lands where it used to.
      grid: { left: '5%', right: '5%', top: '15%', bottom: '15%', containLabel: false },
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        ...ECHARTS_TOOLTIP_STYLE,
        axisPointer: {
          type: 'line',
          lineStyle: { color: CROSSHAIR_COLOR, opacity: 0.8, width: 1 },
        },
        formatter: (params) => {
          const list = Array.isArray(params) ? params : [params];
          if (list.length === 0) return '';
          const header = `<div style="margin-bottom:4px;font-weight:600">${escapeHtml(list[0].axisValueLabel)}</div>`;
          const rows = list
            .map((item) => {
              const value = typeof item.value === 'number' ? item.value.toLocaleString('en-US') : item.value;
              return `<div>${item.marker}${escapeHtml(cutString(item.seriesName, 60))}: <b>${escapeHtml(value)}</b></div>`;
            })
            .join('');
          return header + rows;
        },
      },
      xAxis: {
        type: 'category',
        data: chartData.dateLabels,
        // Centre the lone dot instead of pinning it to the left edge.
        boundaryGap: isSingleDate,
        axisLabel: { color: AXIS_TEXT_COLOR, fontSize: 13, hideOverlap: true },
        axisLine: { lineStyle: { color: GRID_LINE_COLOR } },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        // Five intervals like the old vAxis (e.g. 0, 2k, 4k, 6k, 8k, 10k).
        splitNumber: 5,
        axisLabel: { color: AXIS_TEXT_COLOR, fontSize: 13, formatter: (val) => formatWithCustomGigaBillion(val) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
      },
      series: chartData.series.map((entry, index) => ({
        id: `series-${index}`,
        name: entry.name,
        type: 'line',
        data: entry.data,
        showSymbol: showPoints,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2 },
        areaStyle: { opacity: AREA_OPACITY },
        // Lets clicks on the line/area (not only on symbols) reach onEvents.
        triggerLineEvent: true,
        cursor: showModal ? 'pointer' : 'default',
        label: {
          show: showValues,
          position: 'top',
          distance: showPoints ? 6 : 2,
          fontSize: showPoints ? 12 : 13,
          fontWeight: 'bold',
          color: '#151419',
          textBorderColor: '#fff',
          textBorderWidth: 2,
          formatter: (params) => String(params.value),
        },
        labelLayout: { hideOverlap: true },
        emphasis: { lineStyle: { width: 2 }, scale: 1.5 },
      })).concat(emptySeries),
    };
  }, [chartData, showValues, showPoints, isSingleDate, showModal, animation]);

  const echartsRef = useRef(null);

  // Clear a previous legend selection so a new dataset starts clean. Guarded
  // so it is a no-op (no re-render) when nothing was changed.
  // NOTE: legend actions trigger a full echarts update, which restarts any
  // running series animation. Never dispatch them right after a data update.
  const resetInteractions = useCallback(() => {
    const chart = echartsRef.current?.getEchartsInstance?.();
    if (!chart) return;
    const current = chart.getOption?.();
    const selected = current?.legend?.[0]?.selected ?? {};
    if (Object.values(selected).some((isSelected) => isSelected === false)) {
      chart.dispatchAction({ type: 'legendAllSelect' });
    }
  }, []);

  // Preferred moment: while the parent refetches, the old data is at rest, so
  // resetting here cannot interrupt the upcoming update animation.
  useEffect(() => {
    if (isLoading) {
      resetInteractions();
    }
  }, [isLoading, resetInteractions]);

  // Fallback for consumers that swap rawdata without a loading phase.
  useEffect(() => {
    resetInteractions();
  }, [chartData, resetInteractions]);

  const openPointModal = useCallback((seriesIndex, dataIndex) => {
    if (!chartData) return;
    const entry = chartData.series[seriesIndex];
    const dateKey = chartData.dates[dataIndex];
    if (!entry || !dateKey) return;
    const seriesName = entry.name;
    const startDate = parseISO(dateKey);
    const endDate = getEndDate(startDate);

    setModal({
      isVisible: true,
      startDate,
      endDate,
      country: breakdown === 'COUNTRY' ? seriesName : null,
      platform: breakdown === 'PLATFORM' ? seriesName : null,
      sourceGroup: breakdown === 'ACTOR_GROUP' ? seriesName === 'Others' ? getOthersSourceGroupsIds() : getSourceGroupId(seriesIndex) : null,
      sourceGroupExclude: breakdown === 'ACTOR_GROUP' && seriesName === 'Others' ? true : false,
      // Kept 1-based for getNarrativeEntity, as the google column index was.
      narrative: isReport ? seriesIndex + 1 : null,
      title: seriesName,
    });

    ampli.track({
      event_type: 'Open wiget popup in event',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        narrative_id: narrative?.id,
        popup_type: 'Evolution chart',
        popup_value: `${breakdown}: ${seriesName},`,
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, breakdown, isReport, narrative?.id, currentUser?.id, currentUser?.workspace_id, rawdata]);

  const onEvents = useMemo(() => {
    if (!showModal) return undefined;
    return {
      click: (params) => {
        if (params?.componentType === 'legend') {
          const chart = echartsRef.current?.getEchartsInstance?.();
          const name = params.value ?? params.name;
          // Undo the default show/hide toggle, then open the modal. As on the
          // google-charts version, a legend click resolves to the first date
          // bucket of the chart.
          if (typeof name === 'string') chart?.dispatchAction({ type: 'legendSelect', name });
          if (typeof params.seriesIndex === 'number') openPointModal(params.seriesIndex, 0);
          return;
        }
        if (params?.componentType !== 'series') return;
        let dataIndex = params.dataIndex;
        // Line/area clicks carry no dataIndex: resolve the nearest date from the
        // click position instead.
        if (dataIndex == null && params.event) {
          const chart = echartsRef.current?.getEchartsInstance?.();
          const point = chart?.convertFromPixel({ seriesIndex: params.seriesIndex }, [params.event.offsetX, params.event.offsetY]);
          dataIndex = point ? Math.round(point[0]) : null;
        }
        if (dataIndex == null) return;
        openPointModal(params.seriesIndex, dataIndex);
      },
    };
  }, [showModal, openPointModal]);

  if (!chartData || !option) {
    return;
  }

  if (rawdata?.objects?.length === 0) {
    return (
      <div className="noDataContainer">
        {t('There is no data to display')}
      </div>
    );
  }

  return (
    <div ref={chartRef} className="evolution-chart">
      <ReactECharts
        ref={echartsRef}
        option={option}
        // Merge updates so series (matched by id) animate into the new data.
        // Do NOT use replaceMerge here: it recreates the series on every
        // setOption (including the one echarts-for-react issues when
        // showLoading flips), replaying the entry animation twice.
        notMerge={false}
        showLoading={isLoading}
        loadingOption={LOADING_OPTION}
        style={{ width: '100%', height: `${CHART_HEIGHT}px` }}
        opts={{ renderer: 'svg' }}
        onEvents={onEvents}
      />
      {modal.isVisible ? (
        <WidgetContentModal
          infoModal={modal.isVisible}
          isShare={isShare}
          tabIndex={1}
          isChart={true}
          title={modal.title || ''}
          platformOption={
            !modal?.platform
              ? null
              : modal.platform === 'Others'
              ? filterPlatforms()
              : [{ value: modal?.platform }]
          }
          countryId={
            !modal?.country
              ? null
              : modal?.country === 'Others'
              ? getAllCountriesId(rawdata.objects)
              : [getCountryId(modal?.country)]
          }
          narrative={
            isReport ? getNarrativeEntity(modal?.narrative) : narrative
          }
          sourceGroup={modal?.sourceGroup ? modal?.sourceGroup : null}
          eventIdsOptions={story ? [story?.id] : null}
          startDate={modal.startDate}
          endDate={modal.endDate}
          contentTypeOptions={null}
          countryIdExclude={modal.country === 'Others' ? true : false}
          sourceGroupExclude={modal?.sourceGroupExclude}
          // isShare={isShare}
          onChange={(bool) => {
            setModal({ isVisible: bool, startDate: null, endDate: null, country: null, title: '' });
          }}
          widgetName={'evolution_chart'}
          showAddToActions={true}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              countries: !modal?.country
                ? null
                : modal?.country === 'Others'
                ? getAllCountriesId(rawdata.objects)
                : [getCountryId(modal?.country)],
              platforms: !modal?.platform
                ? null
                : modal.platform === 'Others'
                ? filterPlatforms()
                : [{ value: modal?.platform }],
              countriesExclude: modal.country === 'Others' ? true : false,
              narrative: isReport
                ? getNarrativeEntity(modal?.narrative)
                : narrative,
              sourceGroup: modal?.sourceGroup ? modal?.sourceGroup : null,
              sourceGroupExclude: modal?.sourceGroupExclude,
              sources: sourceId ? sourceId : null,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              countries: !modal?.country
                ? null
                : modal?.country === 'Others'
                ? getAllCountriesId(rawdata.objects)
                : [getCountryId(modal?.country)],
              platforms: !modal?.platform
                ? null
                : modal.platform === 'Others'
                ? filterPlatforms()
                : [{ value: modal?.platform }],
              countriesExclude: modal.country === 'Others' ? true : false,
              narrative: isReport
                ? getNarrativeEntity(modal?.narrative)
                : narrative,
              sourceGroup: modal?.sourceGroup ? modal?.sourceGroup : null,
              sourceGroupExclude: modal?.sourceGroupExclude,
              sources: sourceId ? sourceId : null,
            });
          }}
        />
      ) : (
        ''
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={addToCaseModal?.narrative?.id}
          eventIds={story ? [story?.id] : null} // to save in case from topic modeling case
          areAllMessagesSelected={true}
          affiliationCountries={null}
          startDate={modal.startDate}
          endDate={modal.endDate}
          sourceGroups={addToCaseModal?.sourceGroup ? addToCaseModal?.sourceGroup : null}
          sourceGroupsExclude={addToCaseModal?.sourceGroupExclude}
          sources={addToCaseModal.sources ? [addToCaseModal.sources] : []}
          countries={
            !addToCaseModal.countries
              ? null
              : addToCaseModal.countries.map((country) => {
                  return country.value;
                })
          }
          countriesExclude={addToCaseModal.countriesExclude}
          platforms={
            !addToCaseModal?.platforms
              ? null
              : addToCaseModal.platforms.map((platform) => {
                  return platformMapping[platform.value];
                })
          }
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              countries: null,
              platforms: null,
              countriesExclude: null,
              narrative: null,
              sourceGroup: null,
              sourceGroupExclude: false,
              sources: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={modal?.startDate}
          endDate={modal?.endDate}
          sourceGroups={addToGroupModal?.sourceGroup ? addToGroupModal?.sourceGroup : null}
          sourceGroupsExclude={addToGroupModal?.sourceGroupExclude}
          sourcesFilter={addToGroupModal.sources ? [addToGroupModal.sources] : []}
          countries={
            !addToGroupModal.countries
              ? null
              : addToGroupModal.countries.map((country) => country.value)
          }
          countriesExclude={addToGroupModal.countriesExclude}
          platforms={
            !addToGroupModal?.platforms
              ? null
              : addToGroupModal.platforms.map((platform) => {
                  return platformMapping[platform.value];
                })
          }
          narrative={addToGroupModal?.narrative}
          eventIds={story ? [story?.id] : null} // to save in group from topic modeling case
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              countries: null,
              platforms: null,
              countriesExclude: null,
              narrative: null,
              sourceGroup: null,
              sourceGroupExclude: false,
              sources: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};
